/**
 * Session-Logger — buffers turns and metadata, posts to /api/session-end at end.
 *
 * Lifecycle (driven by main.ts):
 *   1. new SessionLogger(sessionId, options)        — set up, capture metadata
 *   2. addTurn('brian'|'user', text, ...)           — called per AVATAR_TRANSCRIPTION/USER_TRANSCRIPTION/sendText
 *   3. markHandoff('safety'|'normal'|'reklamation') — when handoff phrase detected
 *   4. addProducts(keys)                            — when product cards get rendered
 *   5. endSession('user_ended'|'avatar_disconnect'|'timeout')
 *      → POST /api/session-end with full payload
 *
 * Consent-aware: if `enabled=false` (user declined logging), all methods are
 * no-ops. Brian still works, nothing leaves the browser.
 *
 * @see netlify/functions/session-end.ts — the endpoint that receives the payload
 */

import { hasLoggingConsent } from "./consent-banner";

interface Turn {
  ts: string;
  speaker: "user" | "brian";
  text: string;
  input_mode?: "voice" | "text";
  rendered_cards?: string[];
}

interface SessionMetadata {
  promptVersion: string;
  sdkVersion: string;
  iframeOrigin: string;
}

type EndReason = "user_ended" | "avatar_disconnect" | "timeout";
type HandoffType = "safety" | "normal" | "reklamation";

export class SessionLogger {
  private readonly sessionId: string;
  private readonly enabled: boolean;
  private readonly startedAt: Date;
  private readonly meta: SessionMetadata;
  private readonly turns: Turn[] = [];
  private handoffType: HandoffType | null = null;
  private productsRecommended: Set<string> = new Set();
  private ended = false;

  constructor(sessionId: string, meta: SessionMetadata) {
    this.sessionId = sessionId;
    this.startedAt = new Date();
    this.enabled = hasLoggingConsent();
    this.meta = meta;

    if (!this.enabled) {
      console.info("[session-logger] Logging disabled — user declined consent.");
    }
  }

  /**
   * Records a single turn (avatar or user). No-op if logging is disabled.
   */
  addTurn(
    speaker: "user" | "brian",
    text: string,
    opts: { input_mode?: "voice" | "text"; rendered_cards?: string[] } = {},
  ): void {
    if (!this.enabled || this.ended) return;
    this.turns.push({
      ts: new Date().toISOString(),
      speaker,
      text,
      ...(opts.input_mode ? { input_mode: opts.input_mode } : {}),
      ...(opts.rendered_cards?.length
        ? { rendered_cards: opts.rendered_cards }
        : {}),
    });
  }

  /**
   * Marks the session as a handoff. Used by the safety/reklamation triggers.
   */
  markHandoff(type: HandoffType): void {
    if (!this.enabled || this.ended) return;
    this.handoffType = type;
  }

  /**
   * Tracks product keys recommended in this session. Called when product cards
   * render. Deduplicated automatically.
   */
  addProducts(keys: string[]): void {
    if (!this.enabled || this.ended) return;
    keys.forEach((k) => this.productsRecommended.add(k));
  }

  /**
   * Ends the session and POSTs the full log to /api/session-end.
   * Only sends once — repeated calls are no-ops.
   *
   * **Two transports** depending on context:
   *   - `useBeacon=false` (default): regular `fetch` with full async error handling.
   *     Used for normal end-button click and SDK SESSION_DISCONNECTED.
   *   - `useBeacon=true`: `navigator.sendBeacon` (synchronous, fire-and-forget,
   *     guaranteed during page unload). Used by the `beforeunload` hook.
   *
   * Errors are logged but never thrown — the user's session-end UX shouldn't fail
   * because the log endpoint is down.
   */
  async endSession(reason: EndReason, useBeacon = false): Promise<void> {
    if (!this.enabled || this.ended) return;
    this.ended = true;

    const endedAt = new Date();
    const payload = {
      session_id: this.sessionId,
      session_data: {
        started_at: this.startedAt.toISOString(),
        ended_at: endedAt.toISOString(),
        duration_sec: Math.round(
          (endedAt.getTime() - this.startedAt.getTime()) / 1000,
        ),
        iframe_origin: this.meta.iframeOrigin,
        user_agent: navigator.userAgent,
        prompt_version: this.meta.promptVersion,
        sdk_version: this.meta.sdkVersion,
        handoff_type: this.handoffType,
        end_reason: reason,
        products_recommended: Array.from(this.productsRecommended),
      },
      turns: this.turns,
    };

    const body = JSON.stringify(payload);

    // sendBeacon is the only reliable transport during page unload —
    // the browser commits the request even after the page is gone.
    if (useBeacon && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon("/api/session-end", blob);
      if (!ok) {
        console.warn("[session-logger] sendBeacon refused the payload");
      }
      return;
    }

    try {
      const res = await fetch("/api/session-end", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        // keepalive lets the request survive a navigation that fires after this
        // call but before the response — belt-and-braces in addition to beacon.
        keepalive: true,
      });
      if (!res.ok && res.status !== 207) {
        console.error("[session-logger] /api/session-end failed:", res.status);
      } else if (res.status === 207) {
        console.warn(
          "[session-logger] Partial success — see Netlify function logs.",
        );
      }
    } catch (err) {
      console.error("[session-logger] Network error on session-end:", err);
    }
  }
}
